import React, { useState } from 'react';

function SkillsStep({ data, updateData, nextStep }) {
  const [newKeyword, setNewKeyword] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newSoftware, setNewSoftware] = useState('');

  // Common suggestions
  const popularKeywords = ['Frontend', 'Backend', 'Full Stack', 'Data Science', 'DevOps', 'Mobile', 'UI/UX', 'Product Manager'];
  const popularSkills = ['JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'AWS', 'Git', 'Agile'];
  const popularSoftware = ['VS Code', 'Figma', 'Slack', 'Jira', 'Docker', 'Postman', 'Adobe Creative Suite', 'Microsoft Office'];

  const addItem = (type, value, setter) => {
    if (!value.trim()) return;
    
    const currentItems = data[type] || [];
    if (!currentItems.includes(value.trim())) {
      updateData({ [type]: [...currentItems, value.trim()] });
    }
    setter('');
  };

  const removeItem = (type, item) => {
    const currentItems = data[type] || [];
    updateData({ [type]: currentItems.filter(i => i !== item) });
  };

  const addSuggestion = (type, suggestion) => {
    const currentItems = data[type] || [];
    if (!currentItems.includes(suggestion)) {
      updateData({ [type]: [...currentItems, suggestion] });
    }
  };

  const handleKeyPress = (e, type, value, setter) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem(type, value, setter);
    }
  };

  return (
    <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 md:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          What are your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">skills</span>?
        </h2>
        <p className="text-gray-400">
          Help us understand your expertise and target roles
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-8">
        {/* Role Keywords */}
        <div>
          <label className="block text-lg font-medium text-gray-300 mb-3">
            Role Keywords
          </label>
          <p className="text-sm text-gray-400 mb-4">
            What types of roles are you targeting? (e.g., Frontend Developer, Product Manager)
          </p>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'roleKeywords', newKeyword, setNewKeyword)}
              className="flex-1 bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15"
              placeholder="Add a role keyword..."
            />
            <button
              onClick={() => addItem('roleKeywords', newKeyword, setNewKeyword)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Add
            </button>
          </div>

          {/* Popular Keywords */}
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Popular keywords:</p>
            <div className="flex flex-wrap gap-2">
              {popularKeywords.map(keyword => (
                <button
                  key={keyword}
                  onClick={() => addSuggestion('roleKeywords', keyword)}
                  className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm hover:bg-gray-600 transition-colors"
                >
                  + {keyword}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Keywords */}
          <div className="flex flex-wrap gap-2">
            {(data.roleKeywords || []).map(keyword => (
              <span
                key={keyword}
                className="inline-flex items-center px-3 py-1 bg-blue-500 bg-opacity-20 text-blue-300 rounded-full text-sm"
              >
                {keyword}
                <button
                  onClick={() => removeItem('roleKeywords', keyword)}
                  className="ml-2 text-blue-400 hover:text-blue-200"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Technical Skills */}
        <div>
          <label className="block text-lg font-medium text-gray-300 mb-3">
            Technical Skills
          </label>
          <p className="text-sm text-gray-400 mb-4">
            Programming languages, frameworks, technologies you know
          </p>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'currentSkills', newSkill, setNewSkill)}
              className="flex-1 bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:bg-opacity-15"
              placeholder="Add a skill..."
            />
            <button
              onClick={() => addItem('currentSkills', newSkill, setNewSkill)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              Add
            </button>
          </div>

          {/* Popular Skills */}
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Popular skills:</p>
            <div className="flex flex-wrap gap-2">
              {popularSkills.map(skill => (
                <button
                  key={skill}
                  onClick={() => addSuggestion('currentSkills', skill)}
                  className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm hover:bg-gray-600 transition-colors"
                >
                  + {skill}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Skills */}
          <div className="flex flex-wrap gap-2">
            {(data.currentSkills || []).map(skill => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 bg-purple-500 bg-opacity-20 text-purple-300 rounded-full text-sm"
              >
                {skill}
                <button
                  onClick={() => removeItem('currentSkills', skill)}
                  className="ml-2 text-purple-400 hover:text-purple-200"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Software Familiarity */}
        <div>
          <label className="block text-lg font-medium text-gray-300 mb-3">
            Software & Tools
          </label>
          <p className="text-sm text-gray-400 mb-4">
            Software, tools, and platforms you're familiar with
          </p>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newSoftware}
              onChange={(e) => setNewSoftware(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'softwareFamiliarity', newSoftware, setNewSoftware)}
              className="flex-1 bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-opacity-15"
              placeholder="Add software or tool..."
            />
            <button
              onClick={() => addItem('softwareFamiliarity', newSoftware, setNewSoftware)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Add
            </button>
          </div>

          {/* Popular Software */}
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Popular tools:</p>
            <div className="flex flex-wrap gap-2">
              {popularSoftware.map(software => (
                <button
                  key={software}
                  onClick={() => addSuggestion('softwareFamiliarity', software)}
                  className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm hover:bg-gray-600 transition-colors"
                >
                  + {software}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Software */}
          <div className="flex flex-wrap gap-2">
            {(data.softwareFamiliarity || []).map(software => (
              <span
                key={software}
                className="inline-flex items-center px-3 py-1 bg-green-500 bg-opacity-20 text-green-300 rounded-full text-sm"
              >
                {software}
                <button
                  onClick={() => removeItem('softwareFamiliarity', software)}
                  className="ml-2 text-green-400 hover:text-green-200"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Continue Button */}
        <div className="pt-6">
          <button
            onClick={nextStep}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200"
          >
            Continue
          </button>
          <p className="text-center text-gray-500 text-sm mt-3">
            Don't worry, you can always update these later
          </p>
        </div>
      </div>
    </div>
  );
}

export default SkillsStep; 